import React, { useState, useEffect } from 'react';
import {
  FaShieldAlt,
  FaBolt,
  FaHeart,
  FaStar,
  FaSkull,
  FaArrowRight,
  FaCrosshairs,
  FaPlusCircle,
} from 'react-icons/fa';
import AbilityTooltip from '../utils/AbilityTooltip';

// Custom empty circle component for Shield ability
const EmptyCircle = ({ size = 18, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Helper function to get abilities from order
function getAbilities(order) {
  if (!order) return [];
  // ✅ FIX: Check instanceAbilities first if hasInstanceModifications is true
  // This ensures dynamically added abilities (e.g., Blitz from Exolot Finisher) are included
  if (order.hasInstanceModifications && Array.isArray(order.instanceAbilities)) {
    return order.instanceAbilities;
  }
  if (Array.isArray(order.abilities)) return order.abilities;
  if (typeof order.ability === "string") return [order.ability];
  if (Array.isArray(order.abilitiesList)) return order.abilitiesList;
  return [];
}

// Get ability icon component
// Backend Ability Relay grants "Mend wounds" (with space); support both for UI display
function getAbilityIcon(abilityName) {
  const iconMap = {
    'Protect': FaShieldAlt,
    'Shield': EmptyCircle,
    'Blitz': FaBolt,
    'Recover': FaHeart,
    'Redeem': FaStar,
    'Fell': FaSkull,
    'Penetrate': FaArrowRight,
    'Sniper': FaCrosshairs,
    'MendWounds': FaPlusCircle,
    'Mend wounds': FaPlusCircle, // Ability Relay Drone grants this exact string
  };
  return iconMap[abilityName] || null;
}

// Ability descriptions (matching highlightAbilities.js exactly)
const ABILITY_DESCRIPTIONS = {
  'Recover': 'Attack anything to heal your general for the same amount',
  'MendWounds': 'This unit heals completely at the beginning of your turn',
  'Mend wounds': 'This unit heals completely at the beginning of your turn',
  'Penetrate': 'Deal excess damage to the opponent',
  'Blitz': 'This unit can attack the turn it has been deployed',
  'Redeem': 'Get the effect when the unit dies',
  'Sniper': 'Instantly kill any unit without a shield',
  'Protect': 'This unit must be attacked first',
  'Shield': 'This unit can avoid damage once',
  'Fell': 'Kill a unit to get the effect',
};

// Get tooltip text for an ability
function getAbilityTooltipText(abilityName) {
  // Handle both "MendWounds" and "Mend wounds" variations
  if (abilityName === 'MendWounds' || abilityName === 'Mend wounds') {
    return ABILITY_DESCRIPTIONS['Mend wounds'];
  }
  
  // Direct lookup
  const description = ABILITY_DESCRIPTIONS[abilityName];
  
  // If no description found, return a fallback
  if (!description) {
    console.warn(`No tooltip description found for ability: ${abilityName}`);
    return `${abilityName} ability`; // Fallback so tooltip still shows
  }
  
  return description;
}

// Component for ability icons on the right side of the order
function AbilityIndicator({ order }) {
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Force re-render when abilities change (e.g., Shield consumed, or Ability Relay grant)
  const instanceAbilitiesKey = Array.isArray(order.instanceAbilities)
    ? order.instanceAbilities.join(',')
    : '';
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [
    order.abilities,
    order.hasShield,
    order.weapons,
    order.hasInstanceModifications,
    instanceAbilitiesKey,
  ]);
  
  const abilities = getAbilities(order);
  
  // Rule 1: Generic implementation for all gained abilities
  // Rule 2: Each ability can only be shown once
  // Rule 3: Never show abilities a unit already had and did not gain
  // Rule 4: Applies to friendly and opponent units equally
  
  // Get gained abilities - abilities that were added after the unit was created
  let gainedAbilities = [];
  
  if (order.originalAbilities) {
    // Friendly unit: compare current abilities with original abilities
    gainedAbilities = abilities.filter(ability => 
      !order.originalAbilities.includes(ability)
    );
  } else {
    // Opponent unit: only show abilities that were clearly granted (e.g. Ability Relay Drone, auras)
    const commonlyGrantedAbilities = [
      'Protect', 'Shield', 'Recover', 'Immobilize', 'Confuse',
      'Mend wounds', 'MendWounds', // Ability Relay grants "Mend wounds"
      'Sniper', 'Penetrate', // Ability Relay can grant these
    ];
    gainedAbilities = abilities.filter(ability => 
      commonlyGrantedAbilities.includes(ability)
    );
  }
  
  // WORKAROUND: Remove duplicates from gained abilities
  // This prevents showing the same ability multiple times due to backend duplication
  let uniqueGainedAbilities = [...new Set(gainedAbilities)];
  
  // Special handling for Shield: only show if hasShield is true (not consumed)
  if (order.hasShield === false && uniqueGainedAbilities.includes('Shield')) {
    uniqueGainedAbilities = uniqueGainedAbilities.filter(ability => ability !== 'Shield');
  }
  
  // Filter to only show abilities that have icons
  const abilitiesWithIcons = uniqueGainedAbilities.filter(ability => getAbilityIcon(ability) !== null);
  
  // Add equipped weapons to the list of gained items (for tooltip display)
  const equippedWeapons = (order.weapons || []).map(weapon => {
    // Parse weapon description for stat bonuses
    const statMatch = weapon.description?.match(/([+-]?\d+)\/([+-]?\d+)/);
    if (statMatch) {
      const attackBonus = parseInt(statMatch[1]);
      const healthBonus = parseInt(statMatch[2]);
      let statText = '';
      if (attackBonus !== 0) statText += `+${attackBonus}`;
      if (healthBonus !== 0) statText += `/${healthBonus}`;
      return `[${weapon.name}] (${statText})`;
    }
    return `[${weapon.name}]`;
  });

  const gainedItems = [
    ...uniqueGainedAbilities,
    ...equippedWeapons
  ];
  
  // Debug: log what abilities we found
  if (process.env.NODE_ENV === 'development') {
    console.log('AbilityIndicator:', {
      orderName: order?.name,
      allAbilities: abilities,
      gainedAbilities: uniqueGainedAbilities,
      abilitiesWithIcons: abilitiesWithIcons,
      hasOriginalAbilities: !!order.originalAbilities,
      originalAbilities: order.originalAbilities,
      tooltipTexts: abilitiesWithIcons.map(ability => ({
        ability,
        tooltipText: getAbilityTooltipText(ability)
      }))
    });
  }
  
  // Only show indicator if there are gained abilities with icons
  if (abilitiesWithIcons.length === 0) {
    return null;
  }
  
  return (
    <>
      {/* Ability icons container below title/cost box, aligned left */}
      <div
        style={{
          position: 'absolute',
          top: '95px', // Just below title/cost box (42px top + ~53px for box height)
          left: '50px', // 20px to the right of title/cost box left margin
          display: 'flex',
          flexDirection: 'row', // Horizontal layout for icons
          gap: '6px',
          alignItems: 'flex-start', // Align to left
          zIndex: 10001, // Above description area (10000) to ensure pointer events work
          pointerEvents: 'auto', // Ensure pointer events work
        }}
      >
        {abilitiesWithIcons.map((ability, index) => {
          const IconComponent = getAbilityIcon(ability);
          if (!IconComponent) return null;
          
          const tooltipText = getAbilityTooltipText(ability);
          
          // Ensure tooltip text is always set - this is critical for tooltip to show
          const finalTooltipText = tooltipText || `${ability} ability`;
          
          // Debug: verify tooltip text is set
          if (!finalTooltipText || finalTooltipText.trim() === '') {
            console.error(`AbilityIndicator: Empty tooltip text for ability "${ability}"`);
          } else {
            console.log(`AbilityIndicator: Rendering icon for "${ability}" with tooltip: "${finalTooltipText.substring(0, 50)}..."`);
          }
          
          return (
            <AbilityTooltip
              key={`${ability}-${index}`}
              tooltipText={finalTooltipText || 'Ability description'}
            >
              <span
                style={{
                  display: 'inline-flex',
                  position: 'relative',
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '4px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  cursor: 'help',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease',
                  verticalAlign: 'middle',
                }}
              >
                <IconComponent size={16} />
              </span>
            </AbilityTooltip>
          );
        })}
      </div>
    </>
  );
}

export default AbilityIndicator;
